import { getCookieAsync } from './helpers';
import { logException } from './logging';
import {
    generateCodeChallengeFromVerifier,
    generateCodeVerifier,
} from './utils/crypto';
import {
    currentDocumentTitle,
    currentHash,
    currentOrigin,
    currentPathname,
    currentSearch,
    getSessionStorage,
    historyReplaceState,
    locationReplace,
    fetch,
} from './utils/windowUtils';
import { TokenFetchData } from './types';
import { Config } from './config';

const GENESYS_SIGN_IN_WITH_ID_TOKEN_WITH_REDIRECT =
    'genesysSignInWithIDTokenHintRedirect';

const inMemoryStore = {
    healthSystemToken: '',
};

export const ifGenesysRedirectSaveAuthCodeToWindow = () => {
    const sessionStorage = getSessionStorage();

    try {
        if (sessionStorage.getItem('grace_gar') !== 'true') {
            return;
        }

        const grace_sp = sessionStorage.getItem('grace_sp');
        const grace_gcv = sessionStorage.getItem('grace_gcv');

        sessionStorage.removeItem('grace_gar');
        sessionStorage.removeItem('grace_sp');
        sessionStorage.removeItem('grace_gcv');

        const hash = currentHash();

        if (hash.length < 2) {
            logException('Error with genesys redirect authentication code', {
                exception: new Error('Invalid location hash'),
            });
            return;
        }

        const values = currentHash().substring(1).split('=');

        if (values.length < 2) {
            logException('Error with genesys redirect authentication code', {
                exception: new Error('Invalid location hash parameter'),
            });
            return;
        }

        const code = values[1];
        const searchParams = `${grace_sp}&resumeGenesys=true`;

        historyReplaceState(
            '',
            currentDocumentTitle(),
            `${currentPathname()}?${searchParams}`
        );

        window.genesysAuth = {
            authCode: code,
            codeVerifier: grace_gcv || '',
            redirectUri: currentOrigin(),
        };
    } catch (err) {
        logException('Error with genesys redirect', {
            exception: err as Error,
        });
    }
};

export const genesysSignInWithHealthSystemIDTokenRedirect = async (
    authorizeWebviewUrl: string,
    liveChatTenantName: string
) => {
    const delay = 500; // 500ms between retries
    const startTimestamp = Date.now();
    // Wait up to 45s to get the cookie. See: https://jira.dig.engineering/browse/BA-7340
    const healthSystemIdToken = await getCookieAsync(
        'health_system_id_token',
        (45 * 1000) / delay,
        delay
    ); // retry up to 45s
    const mobileAppAuthClientId = await getCookieAsync(
        'mobile_app_auth_client_id',
        (45 * 1000) / delay,
        delay
    ); // retry up to 45s

    if (!healthSystemIdToken || healthSystemIdToken === '') {
        logException('Error with live chat web view authentication', {
            exception: new Error(
                `Invalid cookie: healthSystemIdToken (Wait: ${
                    Date.now() - startTimestamp
                }ms)`
            ),
        });
        return false;
    }

    if (!mobileAppAuthClientId || mobileAppAuthClientId === '') {
        logException('Error with live chat web view authentication', {
            exception: new Error(
                `Invalid cookie: mobileAppAuthClientId (Wait: ${
                    Date.now() - startTimestamp
                }ms)`
            ),
        });
        return false;
    }

    if (authorizeWebviewUrl === '') {
        logException('Error with live chat web view authentication', {
            exception: new Error(
                `Invalid authorization url: authorizeWebviewUrl (Wait: ${
                    Date.now() - startTimestamp
                }ms)`
            ),
        });
        return false;
    }

    const searchParams = new URLSearchParams(currentSearch());
    const sessionStorage = getSessionStorage();

    sessionStorage.setItem('grace_sp', searchParams.toString());
    sessionStorage.setItem('grace_lctn', liveChatTenantName);
    sessionStorage.setItem(GENESYS_SIGN_IN_WITH_ID_TOKEN_WITH_REDIRECT, 'true');

    const params = [];
    params.push(`client_id=${mobileAppAuthClientId}`);
    params.push(`scope=openid`);
    params.push(`redirect_uri=${currentOrigin()}`);
    params.push(`prompt=login`);
    params.push(`response_type=code`);
    params.push(`id_token_hint=${healthSystemIdToken}`);

    locationReplace(`${authorizeWebviewUrl}?${params.join('&')}`);
};

export const genesysAuthRedirectIfTokenRedirectSet = async (config: Config) => {
    const sessionStorage = getSessionStorage();

    if (!sessionStorage.getItem(GENESYS_SIGN_IN_WITH_ID_TOKEN_WITH_REDIRECT)) {
        return false;
    }

    // Get live chat tenant
    if (!sessionStorage.getItem('grace_lctn')) {
        logException('Error with genesys authentication redirect', {
            exception: new Error(
                'Invalid parameter: grace_lctn. Grace live chat tenant is not set'
            ),
        });
        return false;
    }
    // Will always be set since we checked above
    const liveChatTenantName = sessionStorage.getItem('grace_lctn') || '';

    // Use tenant to derive other values
    const genesysClientId = config.genesys[liveChatTenantName].genesysClientId;
    const authorizeGenesysUrl =
        config.genesys[liveChatTenantName].authorizeGenesysUrl;

    if (!genesysClientId || genesysClientId === '') {
        logException('Error with genesys authentication redirect', {
            exception: new Error('Invalid parameter: genesysClientId'),
        });
        return false;
    }

    if (!authorizeGenesysUrl || authorizeGenesysUrl === '') {
        logException('Error with genesys authentication redirect', {
            exception: new Error('Invalid parameter: authorizeGenesysUrl'),
        });
        return false;
    }

    sessionStorage.removeItem(GENESYS_SIGN_IN_WITH_ID_TOKEN_WITH_REDIRECT);
    sessionStorage.removeItem('grace_lctn');

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallengeFromVerifier(codeVerifier);

    sessionStorage.setItem('grace_gcv', codeVerifier);
    sessionStorage.setItem('grace_gar', 'true');

    const params = [];
    params.push(`client_id=${genesysClientId}`);
    params.push(`response_type=code`);
    params.push(`response_mode=fragment`);
    params.push(`scope=openid`);
    params.push(`redirect_uri=${window.location.origin}`);
    params.push(`code_challenge_method=S256`);
    params.push(`code_challenge=${codeChallenge}`);

    locationReplace(`${authorizeGenesysUrl}?${params.join('&')}`);

    return true;
};

export const setHealthSystemInformationToGenesysAuth = (
    healthSystemToken: string | null
) => {
    if (!window.genesysAuth || !healthSystemToken) {
        return;
    }
    inMemoryStore.healthSystemToken = healthSystemToken;
};

export const validateAuthCodeIdentity = async (
    authorizeTokenUrl: string,
    liveChatTenantName: string,
    genesysAuthCode: string | undefined,
    genesysCodeVerifier: string | undefined,
    redirectUri: string | undefined
) => {
    try {
        const result = await tokenFetchUsingGenesysAuthCode(authorizeTokenUrl, {
            liveChatTenantName,
            code: genesysAuthCode || '',
            codeVerifier: genesysCodeVerifier || '',
            redirectUri: redirectUri || '',
        });
        return result.valid;
    } catch (err: unknown) {
        logException('Error in validateAuthCodeIdentity', {
            exception: new Error(
                err instanceof Error ? err.message : String(err)
            ),
        });
    }
    return false;
};

export const tokenFetchUsingGenesysAuthCode = async (
    authorizeTokenUrl: string,
    tokenFetchData: TokenFetchData
) => {
    try {
        const data = {
            redirect_uri: tokenFetchData.redirectUri,
            code: tokenFetchData.code,
            code_verifier: tokenFetchData.codeVerifier,
            tenant: tokenFetchData.liveChatTenantName,
        } as any;
        const response = await fetch(authorizeTokenUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: inMemoryStore.healthSystemToken,
            },
            body: JSON.stringify(data),
        });
        const result = await response.json();
        return result;
    } catch (err: unknown) {
        logException('Error in tokenFetchUsingGenesysAuthCode', {
            exception: new Error(
                err instanceof Error ? err.message : String(err)
            ),
        });
    }
};
